import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import type { PanelUser, ServerNode } from "../types";
import { ServersPage } from "./ServersPage";
import { IsolationPage } from "./IsolationPage";
import { WebhooksPage } from "./WebhooksPage";
import { LicensesPage } from "./LicensesPage";
import { PolishPage } from "./PolishPage";

export type AdvancedTab =
  | "overview"
  | "cluster"
  | "limits"
  | "integrations"
  | "licenses"
  | "appearance";

type AdvancedSettings = {
  clusterEnabled: boolean;
  resourceLimitsEnabled: boolean;
};

/**
 * W9 — Advanced / optional WHM tools.
 * Cluster (multi-server) is off by default; power users enable it here.
 */
export function AdvancedPage({
  accounts,
  servers,
  onRefresh,
  initialTab,
  serversHandlers,
  authorityMode = "master"
}: {
  accounts: PanelUser[];
  servers: ServerNode[];
  onRefresh: () => Promise<void>;
  initialTab?: AdvancedTab;
  /** customer nodes must never see Owner license registry */
  authorityMode?: "master" | "customer";
  serversHandlers: {
    onCreate: (data: {
      name: string;
      hostname: string;
      agentUrl: string;
      agentToken: string;
      role: "primary" | "node";
      region: string;
      notes: string;
      maxAccounts: number;
      isDefault: boolean;
    }) => Promise<void>;
    onUpdate: (
      id: string,
      data: Partial<{
        hostname: string;
        agentUrl: string;
        agentToken: string;
        region: string;
        notes: string;
        maxAccounts: number;
        isDefault: boolean;
        status: string;
      }>
    ) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    onProbe: (id: string) => Promise<void>;
    onProbeAll: () => Promise<void>;
  };
}) {
  const isCustomer = authorityMode === "customer";
  const safeInitial: AdvancedTab =
    isCustomer && initialTab === "licenses"
      ? "overview"
      : initialTab || "overview";
  const [tab, setTab] = useState<AdvancedTab>(safeInitial);
  const [advanced, setAdvanced] = useState<AdvancedSettings | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api<{ advanced: AdvancedSettings }>(
        "/api/v1/server-config/advanced"
      );
      setAdvanced(res.advanced);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!initialTab) return;
    if (isCustomer && initialTab === "licenses") {
      setTab("overview");
      return;
    }
    setTab(initialTab);
  }, [initialTab, isCustomer]);

  // Customer nodes cannot stay on licenses tab
  useEffect(() => {
    if (isCustomer && tab === "licenses") setTab("overview");
  }, [isCustomer, tab]);

  // If cluster disabled and user lands on cluster tab, stay but show gate
  useEffect(() => {
    if (
      advanced &&
      !advanced.clusterEnabled &&
      tab === "cluster" &&
      initialTab !== "cluster"
    ) {
      /* allow viewing gate */
    }
  }, [advanced, tab, initialTab]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!advanced) return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{ advanced: AdvancedSettings }>(
        "/api/v1/server-config/advanced",
        {
          method: "PATCH",
          body: JSON.stringify(advanced)
        }
      );
      setAdvanced(res.advanced);
      setMsg("Advanced settings saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: AdvancedTab; label: string; hide?: boolean }[] = [
    {
      id: "licenses",
      label: "Licenses (Server IPs)",
      // Owner Master Panel only — never on customer installs
      hide: isCustomer
    },
    { id: "overview", label: "Overview" },
    { id: "cluster", label: "Server Nodes" },
    {
      id: "limits",
      label: "Resource Limits",
      hide: advanced ? !advanced.resourceLimitsEnabled : false
    },
    { id: "integrations", label: "Integrations" },
    { id: "appearance", label: "Appearance" }
  ];

  const licensesTab = tab === "licenses" && !isCustomer;

  return (
    <>
      <div className={`topbar${licensesTab ? " adv-topbar-licenses" : ""}`}>
        <div>
          <h1>Advanced</h1>
          {licensesTab ? (
            <p>
              Manage installations, trial policy, and package assignments.
            </p>
          ) : (
            <p>
              Cluster, resource limits, integrations, and appearance
              {isCustomer
                ? "."
                : ". License registry is available to the Owner Super Admin only."}
            </p>
          )}
        </div>
        {!licensesTab ? (
          <div className="topbar-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                void load();
                void onRefresh();
              }}
            >
              Refresh
            </button>
          </div>
        ) : null}
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="chip-row" style={{ marginBottom: licensesTab ? 10 : 14 }}>
        {tabs
          .filter((t) => !t.hide)
          .map((t) => (
            <button
              key={t.id}
              type="button"
              className={`chip ${tab === t.id ? "chip-active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
              {t.id === "cluster" && advanced && !advanced.clusterEnabled
                ? " · off"
                : ""}
            </button>
          ))}
      </div>

      {tab === "overview" && advanced ? (
        <form className="panel-form" onSubmit={(e) => void save(e)}>
          <div className="panel-form-title">Feature flags</div>
          <p className="card-meta" style={{ marginBottom: 12 }}>
            These flags only affect the WHM UI. Local single-node hosting always
            works without cluster mode.
          </p>
          <div className="feature-check-grid">
            <label className="feature-check">
              <input
                type="checkbox"
                checked={advanced.clusterEnabled}
                onChange={(e) =>
                  setAdvanced((a) =>
                    a ? { ...a, clusterEnabled: e.target.checked } : a
                  )
                }
              />
              <span>
                <strong>Enable Server Nodes (cluster)</strong>
                <br />
                <span className="faint">
                  Multi-server: add remote agents, place accounts, probe health.
                  Leave off for standard shared hosting.
                </span>
              </span>
            </label>
            <label className="feature-check">
              <input
                type="checkbox"
                checked={advanced.resourceLimitsEnabled}
                onChange={(e) =>
                  setAdvanced((a) =>
                    a
                      ? { ...a, resourceLimitsEnabled: e.target.checked }
                      : a
                  )
                }
              />
              <span>
                <strong>Resource Limits (CloudLinux LVE)</strong>
                <br />
                <span className="faint">
                  CageFS / LVE limits and PHP Selector. Works in shadow mode
                  without native CloudLinux.
                </span>
              </span>
            </label>
          </div>
          <button
            type="submit"
            className="btn btn-primary btn-inline"
            disabled={busy}
            style={{ marginTop: 14 }}
          >
            {busy ? "Saving…" : "Save advanced settings"}
          </button>

          <div className="whm-stats" style={{ marginTop: 20 }}>
            <div className="whm-stat">
              <span className="whm-stat-label">Cluster</span>
              <span className="whm-stat-value" style={{ fontSize: 18 }}>
                {advanced.clusterEnabled ? "Enabled" : "Disabled"}
              </span>
              <span className="whm-stat-meta">
                {servers.length} node{servers.length === 1 ? "" : "s"} registered
              </span>
            </div>
            <div className="whm-stat">
              <span className="whm-stat-label">Accounts</span>
              <span className="whm-stat-value">
                {accounts.filter((a) => a.role === "user").length}
              </span>
              <span className="whm-stat-meta">Hosting users</span>
            </div>
            <div className="whm-stat">
              <span className="whm-stat-label">Default node</span>
              <span className="whm-stat-value" style={{ fontSize: 16 }}>
                {servers.find((s) => s.isDefault)?.name ||
                  servers[0]?.name ||
                  "local"}
              </span>
              <span className="whm-stat-meta mono">
                {servers.find((s) => s.isDefault)?.agentUrl ||
                  servers[0]?.agentUrl ||
                  "127.0.0.1:7420"}
              </span>
            </div>
          </div>
        </form>
      ) : null}

      {tab === "cluster" ? (
        advanced && !advanced.clusterEnabled ? (
          <ClusterGate
            onEnable={async () => {
              setBusy(true);
              setError("");
              try {
                const res = await api<{ advanced: AdvancedSettings }>(
                  "/api/v1/server-config/advanced",
                  {
                    method: "PATCH",
                    body: JSON.stringify({ clusterEnabled: true })
                  }
                );
                setAdvanced(res.advanced);
                setMsg("Cluster mode enabled");
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
              } finally {
                setBusy(false);
              }
            }}
            busy={busy}
          />
        ) : (
          <ServersPage
            servers={servers}
            onRefresh={onRefresh}
            {...serversHandlers}
          />
        )
      ) : null}

      {tab === "limits" ? (
        <IsolationPage accounts={accounts} onRefresh={onRefresh} />
      ) : null}

      {tab === "integrations" ? <WebhooksPage /> : null}

      {licensesTab ? <LicensesPage embedded /> : null}

      {tab === "appearance" ? (
        <PolishPage isStaff={true} />
      ) : null}
    </>
  );
}

function ClusterGate({
  onEnable,
  busy
}: {
  onEnable: () => Promise<void>;
  busy: boolean;
}) {
  return (
    <section className="card">
      <div className="card-label">Server Nodes · disabled</div>
      <p className="card-meta" style={{ marginBottom: 12 }}>
        RealPanel defaults to <strong>one local hosting engine</strong> (this
        machine). Multi-server cluster is optional for operators who run several
        agents and place accounts across nodes.
      </p>
      <ul className="card-meta" style={{ marginBottom: 16, paddingLeft: 18 }}>
        <li>Register remote agents by URL + token</li>
        <li>Probe health and set a default placement node</li>
        <li>Route account setup to the chosen node</li>
      </ul>
      <button
        type="button"
        className="btn btn-primary btn-inline"
        disabled={busy}
        onClick={() => void onEnable()}
      >
        {busy ? "Enabling…" : "Enable Server Nodes"}
      </button>
    </section>
  );
}

export function advancedTabFromNav(
  nav: string,
  hint?: string
): AdvancedTab {
  if (hint === "overview" || hint === "cluster" || hint === "limits") {
    return hint;
  }
  if (hint === "integrations" || hint === "licenses" || hint === "appearance") {
    return hint;
  }
  if (nav === "servers") return "cluster";
  if (nav === "isolation") return "limits";
  if (nav === "webhooks") return "integrations";
  if (nav === "licenses") return "licenses";
  if (nav === "polish") return "appearance";
  return "overview";
}
